import { getCostHistory, getCostSummary } from "../api/dashboard";
import { useAsync } from "../hooks/useAsync";

/**
 * Cost Summary 页面,对应产品文档 §8.2(v2.10 新增,v2.11 增加历史每月费用合计)。
 *
 * 与 Dashboard 页原"成本趋势"不同:那个是平台账号自己按 Kiro 服务过滤的
 * 近 30 天每日趋势;这里是平台账号 + 每一个纳管目标账号各自的
 * Month-to-date(本月至今)总费用,用户明确要求"每一个 AWS ID 的费用数据,
 * 包括部署管理平台的 AWS ID"。
 *
 * v2.11:下方新增"历史每月费用合计"区块,展示每个月各账号费用 + 当月合计
 * (数据来自每日定时任务写入的 kiro_cost_snapshots 快照表)。
 */
export function CostSummaryPage() {
  const summary = useAsync(() => getCostSummary(), []);
  const history = useAsync(() => getCostHistory(12), []);

  if (summary.loading) return <div>加载中...</div>;
  if (summary.error) return <div className="error-banner">{summary.error}</div>;
  if (!summary.data) return null;

  const d = summary.data;
  const totalAvailable = d.accounts.filter((a) => a.available).reduce((sum, a) => sum + a.amount_usd, 0);

  return (
    <div>
      <h2 className="section-title">Cost Summary</h2>

      <div className="card" style={{ marginBottom: 16 }}>
        <p style={{ fontSize: 13, color: "#64748b", lineHeight: 1.6 }}>
          统计范围:{d.period_start} 至 {d.period_end}(Month-to-date,本月至今,不含今天完整数据 —— Cost
          Explorer 存在 T+1 结算延迟)。金额为该 AWS 账号本月产生的全部费用(不仅限于 Kiro 相关费用)。
        </p>
      </div>

      <div className="card-grid">
        <div className="card">
          <div style={{ fontSize: 13, color: "#64748b" }}>合计(仅统计数据可用的账号)</div>
          <div style={{ fontSize: 28, fontWeight: 700 }}>${totalAvailable.toLocaleString()}</div>
        </div>
      </div>

      <div className="card">
        <div className="section-title">各账号 Month-to-date 费用</div>
        <table>
          <thead>
            <tr>
              <th>账号 ID</th>
              <th>类型</th>
              <th>费用(USD)</th>
            </tr>
          </thead>
          <tbody>
            {d.accounts.map((a) => (
              <tr key={a.account_id}>
                <td>{a.account_id}</td>
                <td>{a.label || "纳管账号"}</td>
                <td>
                  {a.available ? (
                    `$${a.amount_usd.toFixed(2)}`
                  ) : (
                    <span style={{ color: "#b45309" }}>数据不可用{a.error_detail ? `: ${a.error_detail}` : ""}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card">
        <div className="section-title">历史每月费用合计</div>
        <p style={{ fontSize: 13, color: "#64748b", lineHeight: 1.6, marginBottom: 8 }}>
          每月各账号总费用及当月合计(每日定时快照,本月为 Month-to-date 持续更新,往月为结算稳定后的最终值)。
          功能上线前的月份没有历史快照数据。
        </p>
        {history.loading && <p>加载中...</p>}
        {history.error && <div className="error-banner">{history.error}</div>}
        {history.data && history.data.months.length === 0 && (
          <p style={{ color: "#64748b", fontSize: 13 }}>暂无历史数据(功能刚上线,需等待每日定时任务积累数据)</p>
        )}
        {history.data && history.data.months.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>月份</th>
                <th>各账号明细</th>
                <th>合计(USD)</th>
              </tr>
            </thead>
            <tbody>
              {history.data.months.map((m) => (
                <tr key={m.month}>
                  <td>{m.month}</td>
                  <td style={{ fontSize: 12, color: "#475569" }}>
                    {m.accounts.map((a) => (
                      <div key={a.account_id}>
                        {a.account_id}
                        {a.label ? `(${a.label})` : ""}: ${a.amount_usd.toFixed(2)}
                      </div>
                    ))}
                  </td>
                  <td style={{ fontWeight: 600 }}>${m.total_usd.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
